import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchSession } from '../lib/session';

type Status = { hasToken: boolean; prefix: string | null; createdAt: string | null; lastUsedAt: string | null };
const fmt = (d: string | null) => (d ? new Date(d).toLocaleString('en-GB') : '—');

export default function Profile() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status | null>(null);
  const [fresh, setFresh] = useState<string | null>(null); // shown once, kept only in memory
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    const s = await fetchSession(); if (s) setEmail(s.email);
    const r = await fetch('/api/profile/mcp-token');
    if (r.ok) setStatus(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    setErr(''); setCopied('');
    const r = await fetch('/api/profile/mcp-token', { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.error || 'Failed'); return; }
    setFresh(d.token); await load();
  }
  async function revoke() {
    setErr(''); setFresh(null);
    const r = await fetch('/api/profile/mcp-token', { method: 'DELETE' });
    if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.error || 'Failed'); return; }
    await load();
  }
  async function copy(text: string, which: string) {
    try { await navigator.clipboard.writeText(text); setCopied(which); } catch { setErr('Copy failed — select and copy manually'); }
  }

  const btn = 'min-h-[48px] rounded-md px-4 font-semibold disabled:opacity-40';
  const handover = fresh ? `Command: npm run mcp  (node ./dist/mcp/server.js, stdio, in trading-console-app)\nMCP_API_TOKEN=${fresh}` : '';

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <main className="mx-auto max-w-xl px-3 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold">Profile</h1>
          <Link to="/" className="text-sm text-zinc-400 underline">Console</Link>
        </div>
        <div className="text-sm text-zinc-400">{email}</div>

        <section className="rounded-lg bg-zinc-900 p-3 space-y-3">
          <h2 className="font-semibold">MCP token</h2>
          {status?.hasToken ? (
            <div className="space-y-1 text-sm">
              <div className="text-xl font-bold tabular-nums">{status.prefix}…</div>
              <div className="text-zinc-400">Created {fmt(status.createdAt)}</div>
              <div className="text-zinc-400">Last used {fmt(status.lastUsedAt)}</div>
            </div>
          ) : <div className="text-sm text-zinc-400">No token</div>}

          {fresh && (
            <div className="space-y-2 rounded-md bg-zinc-950 p-3">
              <div className="text-sm font-semibold">Shown once — copy now</div>
              <div className="break-all font-mono text-sm">{fresh}</div>
              <div className="whitespace-pre-wrap break-all font-mono text-xs text-zinc-400">{handover}</div>
              <div className="grid grid-cols-2 gap-2">
                <button className={`${btn} bg-zinc-100 text-zinc-900`} onClick={() => copy(fresh, 'token')}>{copied === 'token' ? 'Copied' : 'Copy token'}</button>
                <button className={`${btn} bg-zinc-800`} onClick={() => copy(handover, 'all')}>{copied === 'all' ? 'Copied' : 'Copy all'}</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button className={`${btn} bg-emerald-600 text-white`} onClick={generate}>{status?.hasToken ? 'Regenerate' : 'Generate'}</button>
            <button className={`${btn} bg-zinc-800`} disabled={!status?.hasToken} onClick={revoke}>Revoke</button>
          </div>
          {err && <div className="text-sm text-red-400">{err}</div>}
        </section>
      </main>
    </div>
  );
}
